import { useState, useMemo, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Pagination } from "@/components/ui/pagination"
import { api, authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"
import { PlusIcon, PencilIcon, Trash2Icon, SearchIcon } from "lucide-react"
import { RestoreIcon } from "@/components/icons/RestoreIcon"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

interface Country  { id: string; name: string; isoCode: string }
interface StateRow { id: string; name: string; code: string | null; deletedAt?: string | null; countryId: string; countryName: string; countryIsoCode: string }

const PAGE_SIZE = 25
const EMPTY = { name: "", code: "", countryId: "" }

async function readApiError(res: Response): Promise<string> {
  const text = await res.text()
  if (!text) return `Error ${res.status}`
  try {
    const parsed = JSON.parse(text) as { error?: string }
    return parsed.error ?? text
  } catch {
    return text
  }
}

export default function StatesPage() {
  const qc = useQueryClient()
  const { can } = usePermissions()

  const [search,       setSearch]       = useState("")
  const [filterCountry,setFilterCountry]= useState("")
  const [filterStatus, setFilterStatus] = useState<"active" | "deleted" | "all">("active")
  const [page,         setPage]         = useState(1)
  const [sheetOpen,    setSheetOpen]    = useState(false)
  const [deleteOpen,   setDeleteOpen]   = useState(false)
  const [editing,      setEditing]      = useState<StateRow | null>(null)
  const [deleting,     setDeleting]     = useState<StateRow | null>(null)
  const [form,         setForm]         = useState({ ...EMPTY })
  const [formError,    setFormError]    = useState<string | null>(null)

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: countries = [] } = useQuery({
    queryKey: ["geo", "countries"],
    queryFn: async () => {
      const r = await authorizedFetch(`${API_URL}/api/geo/countries`)
      return r.json() as Promise<Country[]>
    },
  })

  const { data: allStates = [], isLoading } = useQuery({
    queryKey: ["geo", "states", filterCountry, filterStatus],
    queryFn: async () => {
      const query: Record<string, string> = {}
      if (filterCountry) query.countryId = filterCountry
      if (filterStatus === "deleted") query.deleted = "true"
      else if (filterStatus === "active") query.deleted = "false"
      // "all" → no enviamos deleted, backend devuelve todos
      const params = new URLSearchParams(query)
      const r = await authorizedFetch(`${API_URL}/api/geo/states${params.size ? `?${params}` : ""}`)
      return r.json() as Promise<StateRow[]>
    },
  })

  // ── Filtrado + paginación ──────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim()
    return allStates.filter(s => {
      const matchSearch  = !term ||
        s.name.toLowerCase().includes(term) ||
        (s.code ?? "").toLowerCase().includes(term)
      return matchSearch
    })
  }, [allStates, search])

  // Resetear página al cambiar filtros
  useEffect(() => { setPage(1) }, [search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // ── Mutations ──────────────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: async (data: typeof EMPTY) => {
      const res = await api.geo.states.$post({ json: data })
      if (!res.ok) throw new Error(await readApiError(res))
      return res.json()
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "states"] }); closeSheet() },
    onError: (e: Error) => setFormError(e.message),
  })

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof EMPTY }) => {
      const res = await api.geo.states[":id"].$put({ param: { id }, json: data })
      if (!res.ok) throw new Error(await readApiError(res))
      return res.json()
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "states"] }); closeSheet() },
    onError: (e: Error) => setFormError(e.message),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.geo.states[":id"].$delete({ param: { id } })
      if (!res.ok) throw new Error(await readApiError(res))
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "states"] }); setDeleteOpen(false); setDeleting(null) },
  })

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      await authorizedFetch(`${API_URL}/api/geo/states/${id}/restore`, { method: "PATCH" })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["geo", "states"] }) },
  })

  // ── Handlers ───────────────────────────────────────────────────────────────
  function closeSheet() { setSheetOpen(false); setEditing(null); setForm({ ...EMPTY }); setFormError(null) }

  function openCreate() {
    setEditing(null); setForm({ name: "", code: "", countryId: filterCountry || "" })
    setFormError(null); setSheetOpen(true)
  }

  function openEdit(s: StateRow) {
    setEditing(s); setForm({ name: s.name, code: s.code ?? "", countryId: s.countryId })
    setFormError(null); setSheetOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setFormError(null)
    if (editing) updateMut.mutate({ id: editing.id, data: form })
    else createMut.mutate(form)
  }

  const isSaving = createMut.isPending || updateMut.isPending

  // Nombre del país seleccionado (para el Select trigger)
  const selectedCountryName = countries.find(c => c.id === filterCountry)?.name
  const formCountryName     = countries.find(c => c.id === form.countryId)?.name

  return (
    <>
      <SiteHeader title="Provincias" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">

        {/* Barra de herramientas */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          {/* Buscador */}
          <div className="relative flex-1 min-w-[200px]">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Buscar por nombre o código..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          {/* Filtro por país */}
          <Select value={filterCountry} onValueChange={(v) => setFilterCountry(v ?? "")}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Todos los países">
                {selectedCountryName ?? (filterCountry ? filterCountry : null)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">Todos los países</SelectItem>
              {countries.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v as "active" | "deleted" | "all"); setPage(1) }}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="deleted">Borrados</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>

          {can("write", "geo") && (
            <Button onClick={openCreate} className="gap-2 shrink-0">
              <PlusIcon className="size-4" /> Nueva provincia
            </Button>
          )}
        </div>

        {/* Tabla */}
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Código</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>País</TableHead>
                {can("write", "geo") && <TableHead className="text-right pr-4 w-24">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={4} className="text-center py-16 text-muted-foreground">Cargando...</TableCell></TableRow>
              ) : paged.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-16 text-muted-foreground">
                  {search || filterCountry ? "Sin resultados para la búsqueda" : "No hay provincias registradas"}
                </TableCell></TableRow>
              ) : paged.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    {s.code
                      ? <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{s.code}</span>
                      : <span className="text-muted-foreground/40 text-xs">—</span>}
                  </TableCell>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded mr-1.5">{s.countryIsoCode}</span>
                    {s.countryName}
                  </TableCell>
                  {can("write", "geo") && (
                    <TableCell className="text-right pr-4">
                      <div className="flex justify-end gap-1">
                        {s.deletedAt ? (
                          <Button variant="ghost" size="icon-sm"
                            onClick={() => restoreMut.mutate(s.id)}
                            disabled={restoreMut.isPending}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" title="Restaurar">
                            <RestoreIcon className="size-3.5" />
                          </Button>
                        ) : (
                          <>
                            <Button variant="ghost" size="icon-sm" onClick={() => openEdit(s)} title="Editar">
                              <PencilIcon className="size-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon-sm"
                              onClick={() => { setDeleting(s); setDeleteOpen(true) }}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10" title="Eliminar">
                              <Trash2Icon className="size-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* Sheet: Crear / Editar */}
      <Sheet open={sheetOpen} onOpenChange={(o) => { if (!o) closeSheet(); else setSheetOpen(true) }}>
        <SheetContent side="right" className="sm:max-w-sm">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar provincia" : "Nueva provincia"}</SheetTitle>
            <SheetDescription>Nombre de la provincia, código ISO 3166-2 y país.</SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4">
            <div className="grid gap-1.5">
              <Label htmlFor="s-name">Nombre</Label>
              <Input id="s-name" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="s-code">Código</Label>
              <Input id="s-code" value={form.code} maxLength={10} placeholder="Ej. AR-B"
                className="uppercase font-mono"
                required
                onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="s-country">País</Label>
              <Select value={form.countryId} onValueChange={v => setForm(f => ({ ...f, countryId: v ?? "" }))}>
                <SelectTrigger id="s-country">
                  <SelectValue placeholder="Seleccioná un país">
                    {formCountryName ?? (form.countryId ? form.countryId : null)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {countries.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {formError && <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>}
            <SheetFooter className="px-0 mt-2">
              <Button type="button" variant="outline" onClick={closeSheet}>Cancelar</Button>
              <Button type="submit" disabled={isSaving || !form.countryId}>
                {isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear provincia"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Dialog: Confirmar eliminación */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar provincia</DialogTitle>
            <DialogDescription>
              ¿Marcás como eliminada <strong>{deleting?.name}</strong>? La provincia quedará como borrada lógica.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose><Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button></DialogClose>
            <Button variant="destructive" disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}>
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
